import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import {
  IProfessional,
  ITreatment,
  ITreatmentItem,
} from '../../../shared/interfaces';
import api from '../../../services/api';
import {
  AButton,
  ATitle,
  Loading,
  MenuOptions,
  ItemStatus,
} from '../../../components';
import {
  CheckCircleOutlined,
  FilterOutlined,
  HistoryOutlined,
  LineChartOutlined,
  PrinterOutlined,
  RedoOutlined,
  SyncOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  Card,
  Col,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Typography,
} from 'antd';
import { currency } from '../../../shared/utils/functions';
import EditEvolution from './Edit';
import HistoricTreatment from './Historic';

const TreatmentEvolution: React.FC = () => {
  type TStatusFilter = 'all' | '0' | '1' | '2';

  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [treatment, setTreatment] = useState<ITreatment | null>(null);
  const [typeEdit, setTypeEdit] = useState<number>();
  const [treatmentItems, setTreatmentItems] = useState<ITreatmentItem[]>([]);
  const [treatmentItem, setTreatmentItem] = useState<ITreatmentItem | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [modalHistoric, setModalHistoric] = useState<boolean>(false);
  const [modalEditEvolution, setModalEditEvolution] = useState<boolean>(false);
  const [updateProfessional, setUpdateProfessional] = useState<boolean>(false);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [statusFilter, setStatusFilter] = useState<TStatusFilter>('all');

  const summary = useMemo(() => {
    const totalItems = treatmentItems.length;
    const inProgress = treatmentItems.filter(
      (item) => item.status === 1,
    ).length;
    const completed = treatmentItems.filter((item) => item.status === 2).length;
    const pending = totalItems - inProgress - completed;
    const averageRank =
      totalItems > 0
        ? treatmentItems.reduce(
            (acc, item) => acc + Number(item.rank || 0),
            0,
          ) / totalItems
        : 0;

    return {
      totalItems,
      inProgress,
      completed,
      pending,
      averageRank,
    };
  }, [treatmentItems]);

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return treatmentItems;
    return treatmentItems.filter(
      (item) => item.status === Number(statusFilter),
    );
  }, [treatmentItems, statusFilter]);

  const currentFilterLabel = useMemo(() => {
    if (statusFilter === 'all') return 'Todos';
    if (statusFilter === '0') return 'Pendentes';
    if (statusFilter === '1') return 'Em andamento';
    return 'Concluídos';
  }, [statusFilter]);

  const loadTreatment = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/treatments/${id}`)
      .then((response) => {
        const { treatment, items } = response.data;
        setTreatment(treatment);
        setTreatmentItems(items);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response.data.message}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const listProfessionals = useCallback(async () => {
    try {
      const response = await api.get(`/professionals/select`);
      setProfessionals(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const handleEdit = useCallback((type: number, item: ITreatmentItem) => {
    setTreatmentItem(item);
    setUpdateProfessional(false);
    setTypeEdit(type);
    setModalEditEvolution(true);
  }, []);

  const handleUpdateProfessional = useCallback((item: ITreatmentItem) => {
    setUpdateProfessional(true);
    setTreatmentItem(item);
    setModalEditEvolution(true);
  }, []);

  const renderModalEditEvolution = () => {
    if (modalEditEvolution) {
      return (
        <EditEvolution
          open={modalEditEvolution}
          close={() => setModalEditEvolution(false)}
          professionals={professionals}
          type={typeEdit}
          treatmentItem={treatmentItem}
          updateProfessional={updateProfessional}
          setTreatmentItems={setTreatmentItems}
        />
      );
    }
  };

  const renderModalHistoricTreatment = () => {
    if (modalHistoric) {
      return (
        <HistoricTreatment
          open={modalHistoric}
          close={() => setModalHistoric(false)}
          treatment={treatment}
        />
      );
    }
  };

  useEffect(() => {
    loadTreatment();
    listProfessionals();
  }, [loadTreatment, listProfessionals]);

  if (loading) return <Loading load />;
  return (
    <>
      <ATitle icon={<LineChartOutlined />}>
        Evolução do {treatment?.type === 1 ? 'tratamento' : 'orçamento'} Nº{' '}
        {treatment?.code}
      </ATitle>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('all')}
            style={
              statusFilter === 'all'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Procedimentos" value={summary.totalItems} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('2')}
            style={
              statusFilter === '2'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Concluídos" value={summary.completed} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('1')}
            style={
              statusFilter === '1'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Em andamento" value={summary.inProgress} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setStatusFilter('0')}
            style={
              statusFilter === '0'
                ? {
                    borderColor: '#1677ff',
                    boxShadow: '0 0 0 1px #1677ff inset',
                  }
                : undefined
            }
          >
            <Statistic title="Pendentes" value={summary.pending} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Rank médio geral"
              value={summary.averageRank.toFixed(1)}
            />
          </Card>
        </Col>
      </Row>

      <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Space direction="vertical" size={8}>
          <Segmented
            options={[
              { label: 'Todos', value: 'all' },
              { label: 'Pendentes', value: '0' },
              { label: 'Em andamento', value: '1' },
              { label: 'Concluídos', value: '2' },
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as TStatusFilter)}
          />
          <Typography.Text type="secondary">
            <FilterOutlined /> Filtro atual: {currentFilterLabel} (
            {filteredItems.length})
          </Typography.Text>
        </Space>
        <MenuOptions
          menu_items={[
            {
              label: 'Atualizar',
              key: '1',
              icon: <RedoOutlined />,
              onClick: () => loadTreatment(),
            },
            {
              label: 'Histórico',
              key: '2',
              icon: <HistoryOutlined />,
              onClick: () => {
                setModalHistoric(true);
              },
            },
            {
              label: 'Imprimir evolução',
              key: '3',
              icon: <PrinterOutlined />,
              onClick: () => {
                navigate(`/treatment/${id}/evolution/print`);
              },
            },
          ]}
          showButtonBack
        />
      </Row>
      <Table
        style={{ width: '100%', marginTop: 20 }}
        scroll={{ y: 350 }}
        columns={[
          {
            title: 'Procedimento',
            dataIndex: 'procedure_name',
          },
          {
            title: 'Profissional',
            dataIndex: 'professional_id',
            render: (value: string) => {
              const name = professionals.find((x) => x.id === value)?.name;
              return <>{name ?? ''}</>;
            },
          },
          {
            title: 'Qtd.',
            dataIndex: 'quantity',
            width: 75,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 160,
            render: (value: number) => <ItemStatus type={value} />,
          },
          {
            title: 'Total',
            width: 120,
            render: (cell: ITreatmentItem) => (
              <>
                {currency(
                  cell.value * cell.quantity - cell.discount * cell.quantity,
                )}
              </>
            ),
          },
          {
            title: 'Opções',
            width: 170,
            render: (cell: ITreatmentItem) => (
              <Space size={8}>
                <AButton
                  type="primary"
                  onClick={() => handleEdit(1, cell)}
                  icon={<SyncOutlined />}
                  title="Em andamento"
                />
                <AButton
                  type="primary"
                  style={{ backgroundColor: '#32cd32' }}
                  onClick={() => handleEdit(2, cell)}
                  icon={<CheckCircleOutlined />}
                  title="Concluído"
                />
                <AButton
                  type="dashed"
                  onClick={() => handleUpdateProfessional(cell)}
                  icon={<UserSwitchOutlined />}
                  title="Alterar Profissional"
                />
              </Space>
            ),
          },
        ]}
        dataSource={filteredItems}
        rowKey="id"
        pagination={false}
        bordered
        locale={{
          emptyText: 'Nenhum procedimento encontrado para este filtro.',
        }}
        title={() => (
          <Row style={{ display: 'flex', justifyContent: 'start' }}>
            <ATitle>Procedimentos inclusos</ATitle>
          </Row>
        )}
        footer={() => {
          return (
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <ATitle>Total</ATitle>
              <ATitle>{currency(treatment?.total || 0)}</ATitle>
            </Row>
          );
        }}
      />
      {renderModalEditEvolution()}
      {renderModalHistoricTreatment()}
    </>
  );
};

export default TreatmentEvolution;
